import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    IconButton,
    Paper,
    Slide,
    Backdrop,
    Checkbox,
    FormGroup
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from '@mui/material/styles';
import theme from '../styles/theme';
import { PrimaryButton } from './';
import axiosClient from '../services/axiosClient';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

interface ScreeningSetupProps {
    open: boolean;
    onClose: () => void;
    isDesktop: boolean;
    listingId: string;
    criteria: any;
    loading: boolean;
    onSuccess?: () => void;
}

const ScreeningSetup: React.FC<ScreeningSetupProps> = ({ open, onClose, isDesktop, listingId, criteria, loading, onSuccess }) => {
    const [selectedValues, setSelectedValues] = useState({
        monthlyIncome: '',
        acceptStudents: '',
        firstTimeRenters: '',
        oneYearJobRequirement: '',
        credit_background_check: '',
        cosigners: ''
    });

    const [petPolicy, setPetPolicy] = useState({
        no_pets_allowed: false,
        cats_allowed: false,
        small_dogs_allowed: false,
        large_dogs_allowed: false
    });

    useEffect(() => {
        if (criteria) {
            setSelectedValues({
                monthlyIncome: criteria.minimum_monthly_income?.toString() ?? '',
                acceptStudents: criteria.accepts_students?.toString() ?? '',
                firstTimeRenters: criteria.accepts_first_time_renters?.toString() ?? '',
                oneYearJobRequirement: criteria.requires_1_year_of_employment?.toString() ?? '',
                credit_background_check: criteria.requires_background_and_credit_check?.toString() ?? '',
                cosigners: criteria.cosigners_okay?.toString() ?? ''
            });
            setPetPolicy({
                no_pets_allowed: criteria.no_pets_allowed ?? false,
                cats_allowed: criteria.cats_allowed ?? false,
                small_dogs_allowed: criteria.small_dogs_allowed ?? false,
                large_dogs_allowed: criteria.large_dogs_allowed ?? false
            });
        }
    }, [criteria]);

    // Handle pet policy toggle with special logic for "no pets allowed"
    const handlePetPolicyToggle = (optionValue: string) => {
        if (optionValue === 'no_pets_allowed') {
            if (petPolicy.no_pets_allowed) {
                // If "no pets allowed" is already selected, just deselect it
                setPetPolicy({ ...petPolicy, no_pets_allowed: false });
            } else {
                // If "no pets allowed" is being selected, deselect all others and select only this
                setPetPolicy({
                    no_pets_allowed: true,
                    cats_allowed: false,
                    small_dogs_allowed: false,
                    large_dogs_allowed: false
                });
            }
        } else {
            // For other options, toggle the value and remove "no_pets_allowed" if selecting
            const newValue = { ...petPolicy, [optionValue]: !petPolicy[optionValue as keyof typeof petPolicy] };

            // Remove "no_pets_allowed" if any other option is selected
            if (newValue[optionValue as keyof typeof petPolicy] && optionValue !== 'no_pets_allowed') {
                newValue.no_pets_allowed = false;
            }

            setPetPolicy(newValue);
        }
    };

    const handleSubmit = async () => {

        const screeningCriteria = {
            minimum_monthly_income: selectedValues.monthlyIncome ? parseInt(selectedValues.monthlyIncome) : null,
            accepts_students: selectedValues.acceptStudents === 'true',
            accepts_first_time_renters: selectedValues.firstTimeRenters === 'true',
            requires_1_year_of_employment: selectedValues.oneYearJobRequirement === 'true',
            requires_background_and_credit_check: selectedValues.credit_background_check === 'true',
            cosigners_okay: selectedValues.cosigners === 'true',
            // Pet policy fields (saved on listing model)
            no_pets_allowed: petPolicy.no_pets_allowed,
            cats_allowed: petPolicy.cats_allowed,
            small_dogs_allowed: petPolicy.small_dogs_allowed,
            large_dogs_allowed: petPolicy.large_dogs_allowed
        };


        try {
            const response = await axiosClient.post(`/listings/${listingId}/screening_criteria`, {
                screening_criteria: screeningCriteria
            });

            if (response.status === 200) {
                if (onSuccess) onSuccess();
                onClose();
            }
        } catch (error) {
            console.error('Failed to save screening criteria:', error);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <ThemeProvider theme={theme}>
            <Backdrop
                open={open}
                sx={{
                    zIndex: theme => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%'
                        }}
                    >
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 6,
                                width: isDesktop ? '500px' : '100%',
                                maxWidth: isDesktop ? '500px' : '100%',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                margin: isDesktop ? 'auto' : 0,
                                position: 'relative',
                            }}
                        >
                            <IconButton
                                onClick={onClose}
                                sx={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '12px',
                                }}
                            >
                                <CloseIcon />
                            </IconButton>

                            <Box display="flex" alignItems="center" mb={3} pr={4}>
                            <VerifiedOutlinedIcon sx={{ color: 'black', mr: 1, fontSize: 40 }} />
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Let AI Screen Prospects
                                </Typography>
                            </Box>
                            {/* Monthly Income Question */}
                            <Box sx={{ mb: 3 }}>
                                <Typography fontWeight={550}>Minimum monthly income required?</Typography>
                                <TextField
                                    sx={{ ml: 0, mt: 1 }}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                                    }}
                                    value={selectedValues.monthlyIncome}
                                    onChange={(e) => setSelectedValues({
                                        ...selectedValues,
                                        monthlyIncome: e.target.value
                                    })}
                                />
                            </Box>

                            {/* Accept Students Question */}
                            <Box sx={{ mb: 3 }}>
                                <Typography fontWeight={550}>Do you accept students?</Typography>
                                <FormControl component="fieldset" sx={{ ml: 0, mt: 1 }}>
                                    <RadioGroup
                                        row
                                        value={selectedValues.acceptStudents || undefined}
                                        onChange={(e) => setSelectedValues({
                                            ...selectedValues,
                                            acceptStudents: e.target.value
                                        })}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="false" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {/* First Time Renters Question */}
                            <Box sx={{ mb: 3 }}>
                                <Typography fontWeight={550}>Do you accept first time renters?</Typography>
                                <FormControl component="fieldset" sx={{ ml: 0, mt: 1 }}>
                                    <RadioGroup
                                        row
                                        value={selectedValues.firstTimeRenters || undefined}
                                        onChange={(e) => setSelectedValues({
                                            ...selectedValues,
                                            firstTimeRenters: e.target.value
                                        })}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="false" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {/* Job Requirement Question */}
                            <Box sx={{ mb: 3 }}>
                                <Typography fontWeight={550}>Do you require applicants to have held a job for a year?</Typography>
                                <FormControl component="fieldset" sx={{ ml: 0, mt: 1 }}>
                                    <RadioGroup
                                        row
                                        value={selectedValues.oneYearJobRequirement || undefined}
                                        onChange={(e) => setSelectedValues({
                                            ...selectedValues,
                                            oneYearJobRequirement: e.target.value
                                        })}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="false" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {/* Background & Credit Check Question */}
                            <Box sx={{ mb: 3 }}>
                                <Typography fontWeight={550}>Do you require background and credit check?</Typography>
                                <FormControl component="fieldset" sx={{ ml: 0, mt: 1 }}>
                                    <RadioGroup
                                        row
                                        value={selectedValues.credit_background_check || undefined}
                                        onChange={(e) => setSelectedValues({
                                            ...selectedValues,
                                            credit_background_check: e.target.value
                                        })}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="false" control={<Radio />} label="No" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>

                            {/* Pet Policy Section */}
                            <Box sx={{ mb: 3 }}>
                                <Typography fontWeight={550} sx={{ mb: 1 }}>Pet Policy</Typography>
                                <FormControl component="fieldset" sx={{ ml: 0 }}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={petPolicy.no_pets_allowed}
                                                    onChange={() => handlePetPolicyToggle('no_pets_allowed')}
                                                />
                                            }
                                            label="No pets allowed"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={petPolicy.cats_allowed}
                                                    onChange={() => handlePetPolicyToggle('cats_allowed')}
                                                />
                                            }
                                            label="Cats allowed"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={petPolicy.small_dogs_allowed}
                                                    onChange={() => handlePetPolicyToggle('small_dogs_allowed')}
                                                />
                                            }
                                            label="Small dogs allowed"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={petPolicy.large_dogs_allowed}
                                                    onChange={() => handlePetPolicyToggle('large_dogs_allowed')}
                                                />
                                            }
                                            label="Large dogs allowed"
                                        />
                                    </FormGroup>
                                </FormControl>
                            </Box>
                            <Box mt={3}>
                                <PrimaryButton
                                    fullWidth
                                    onClick={handleSubmit}
                                >
                                    Confirm and Submit
                                </PrimaryButton>
                            </Box>
                        </Paper>
                    </Box>
                </Slide>
            </Backdrop>
        </ThemeProvider>
    );
};

export default ScreeningSetup;